'use client';

import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  KissanEventType,
  useAppPersistenceStore,
  useEdTechProductStore,
  useGlobalLoginContext,
} from '@ninjacart/nc-nk-store';
import {
  CustomizedCarousel,
  ScrollView,
  useTheme,
  useWindowDimensions,
  YStack
} from '@ninjacart/nc-nk-ui';
import { InAppBrowser, useRouter } from '@ninjacart/nc-nk-web-deps';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Platform } from 'react-native';

interface IBannerContentData {
  content: IBannerContent[];
}
interface IBannerContent {
  url?: string;
  path?: string;
  type?: string;
  ctaEnabled?: boolean;
  carouselName?: string;
  logInEnabled?: boolean;
}

type AdaptiveCarouselProps = {
  carouselData: IBannerContentData;
  sourcePage: string;
  setVideoFeedIndex?: (index: number) => void;
};

export const AdaptiveCarousel: React.FC<AdaptiveCarouselProps> = ({
  carouselData,
  sourcePage,
  setVideoFeedIndex,
}) => {
  if (!carouselData || !carouselData.content || carouselData.content.length === 0) {
    return null;
  }
  const scrollRef = useRef<ScrollView | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { width } = useWindowDimensions();
  const theme = useTheme();
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const processEvents = useRef(useQueuedEventStore((state) => state.processEvents)).current;
  const fields = useGlobalLoginContext(['openLoginSheet','previousPage', 'navigationParams']);
  const setNavigationParams = fields.navigationParams.set;
  const screenName = useEdTechProductStore((state) => state.screenName);
  const { push } = useRouter();
  const { t } = useTranslation();
  const appLanguage = useAppPersistenceStore.getState()?.appLanguage ?? 'en';

  const navigateToCarouselScreen = async (item, index) => {
    if (item?.carouselName) {
      handleCarouselClickEvent(item.carouselName, index);
    }

    if (!item?.path) return;

    // 1) Handle login gating
    if (item.logInEnabled) {
      fields.openLoginSheet.set(true);
      fields['previousPage'].set('adaptive carousel page');
      setNavigationParams({
        name: '',
        routeName: { name: item.path },
        description: `${t('page.pleaseLoginForBetterExperience')}`,
        screenName: 'adaptive carousel page',
      });
      return;
    }

  let rawUrl = `${item.path}&pageName=${screenName}${Platform.OS !== 'web' ? `&lang=${appLanguage}` : ''}`;
    if (setVideoFeedIndex) {
      setVideoFeedIndex(-1);
    }
    const isEncoded = rawUrl.startsWith('https');
    const url = isEncoded ? decodeURIComponent(rawUrl) : rawUrl;
    // await WebBrowser.openBrowserAsync(rawUrl);

    // 4) Now branch on whether it really starts with “http(s)://”
    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        await InAppBrowser.open(url, {
          toolbarColor: '#375740ff',
          enableUrlBarHiding: true,
          showInRecents: true,
        });

        // await
        // reset your feed index
      } catch (e) {
        console.warn('InAppBrowser error, falling back to Linking.openURL', e);
        Linking.openURL(url);
      }
    } else {
      // Anything else, just pass to React Navigation / web Linking
      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        const canOpen = await Linking.canOpenURL(url);
        if (canOpen) {
          Linking.openURL(url);
        } else {
          // as a last‑ditch, push into your RN stack
          push(`/${url}`);
        }
      }
    }
  };

  const handleCarouselClickEvent = (carouselName, index) => {
    let dynamicEventName;
    if (screenName == 'feed page') {
      dynamicEventName = `${KissanEventType.HOMEPAGE_BANNER_CLICK}${index}`;
    } else if (screenName == 'product list page') {
      dynamicEventName = `${KissanEventType.PRODUCTS_BANNER_CLICK}${index}`;
    }
    const additionalDetails = [
      addEventParam('referenceKey4', 'carouselName'),
      addEventParam('referenceValue4', carouselName),
      addEventParam('index', index),
    ];
    const carouselClickEvent: INinjaEvent = {
      name: dynamicEventName,
      screenDetails: { screen: screenName },
      eventParams: additionalDetails,
    };
    processEvents([carouselClickEvent]);
  };

  const captureImpressionEvent = (index: number) => {
    const dataViewedMoreThanOneSecEvent: INinjaEvent = {
      name: KissanEventType.DATA_VIEWED_MORETHAN_ONE_SECOND,
      screenDetails: { screen: `Banner ${sourcePage} page` },
      eventParams: [addEventParam('itemIndex', `${index}`), addEventParam('type', `Banner`)],
    };
    enqueue(dataViewedMoreThanOneSecEvent);
  };

  useEffect(() => {
    captureImpressionEvent(0);
  }, []);

  return (
    <YStack flex={1} backgroundColor={'$homeStatusBarColor'} minHeight={200}>
      <CustomizedCarousel
        images={carouselData?.content}
        onImagePress={navigateToCarouselScreen}
        autoPlayInterval={3000}
        dotSize={10}
      />
    </YStack>
  );
};
